/*!
 * TAE accessibility toolbar v1.0: display preferences for visitors (replaces the UserWay widget).
 *
 * Needs snippets/accessibility-toolbar.liquid rendered in <head>. The snippet holds the page-level CSS
 * (all keyed off data-tae-* attributes on <html>) and a tiny inline script that re-applies saved
 * preferences before first paint. This file builds the panel and does the work that needs the DOM:
 * it measures each text element's original font size and line height, and which elements have
 * solid backgrounds, so text can be scaled and colours swapped without breaking the layout.
 *
 * Preferences are stored on the visitor's device only (localStorage "tae-a11y:v1").
 * Public hooks: window.TAEA11y.open() / .close() / .reset() / .get() / .set(key, value);
 * a "tae-a11y:change" event on window after every change.
 */
(function () {
  'use strict';
  // Browsers without <dialog> or shadow DOM (Safari before 15.4, Firefox before 98) also lack the CSS the
  // settings rely on, so the toolbar stays off there and the page is left exactly as it is.
  if (window.TAEA11y || !window.addEventListener || typeof window.HTMLDialogElement !== 'function' || !document.documentElement.attachShadow) return;

  var script = document.currentScript;
  var KEY = 'tae-a11y:v1';
  var A = 'data-tae-';
  var STEPS = [100, 110, 125, 150, 175, 200];
  var CHOICES = { lh: ['150', '175', '200'], contrast: ['dark', 'light', 'invert'], saturation: ['low', 'high', 'mono'] };
  var TOGGLES = ['spacing', 'font', 'links', 'pause', 'guide', 'cursor', 'images'];
  var KEYS = ['text', 'lh', 'contrast', 'saturation'].concat(TOGGLES);
  // <html> attributes removed while measuring, because their CSS changes what gets measured.
  var MEASURED = ['text', 'lh', 'spacing', 'font', 'contrast', 'images'];
  // Per-element marks written by measure().
  var MARKS = ['t', 'lp', 'i', 'tw', 'th', 'bg', 'g', 'pb', 'pa', 'bgi', 'bgt'];
  // Enlarged text can squeeze buttons below 24 px (WCAG 2.5.8). Buttons that are at least 24 px before any change
  // keep that as a floor; buttons that were already smaller are the site's own issue and are left as they are.
  var BUTTONS = 'button, [role="button"]:not(:empty), input[type="button"], input[type="submit"], input[type="reset"]';
  var SKIP = {
    SCRIPT: 1, STYLE: 1, LINK: 1, META: 1, NOSCRIPT: 1, TEMPLATE: 1, BR: 1, WBR: 1, IMG: 1, PICTURE: 1,
    SOURCE: 1, TRACK: 1, VIDEO: 1, AUDIO: 1, CANVAS: 1, IFRAME: 1, OBJECT: 1, EMBED: 1, PARAM: 1, 'TAE-A11Y': 1
  };
  var ICON_FONT = /icon|awesome|glyph|material symbols|material icons|jdgm|judgeme|socicon|fontello|feather|dashicons|star/i;
  var TRIGGERS = '#Accessibility, [data-tae-a11y-open], a[href="#accessibility"]';
  var FINE_POINTER = !window.matchMedia || window.matchMedia('(any-pointer: fine)').matches;

  var STR = {
    en: {
      launcher: 'Accessibility settings', title: 'Accessibility',
      intro: 'Change how this site looks for you. Your choices are saved on this device.',
      close: 'Close', text: 'Text', size: 'Text size', smaller: 'Decrease text size', larger: 'Increase text size',
      lh: 'Line height', lhOpts: ['Default', '1.5×', '1.75×', '2×'],
      spacing: 'Text spacing', font: 'Readable font',
      color: 'Color and contrast', contrast: 'Contrast', contrastOpts: ['Default', 'Dark', 'Light', 'Invert'],
      saturation: 'Saturation', saturationOpts: ['Default', 'Low', 'High', 'Grayscale'],
      links: 'Highlight links',
      reading: 'Reading and motion', pause: 'Pause animations', guide: 'Reading guide', cursor: 'Big cursor', images: 'Hide images',
      reset: 'Reset all', statement: 'Accessibility statement',
      sizeNow: 'Text size {v}', resetDone: 'All settings reset', pct: '{n}%'
    },
    es: {
      launcher: 'Ajustes de accesibilidad', title: 'Accesibilidad',
      intro: 'Cambia cómo se ve este sitio. Tus preferencias se guardan en este dispositivo.',
      close: 'Cerrar', text: 'Texto', size: 'Tamaño del texto', smaller: 'Reducir el tamaño del texto', larger: 'Aumentar el tamaño del texto',
      lh: 'Interlineado', lhOpts: ['Normal', '1,5×', '1,75×', '2×'],
      spacing: 'Espaciado del texto', font: 'Fuente legible',
      color: 'Color y contraste', contrast: 'Contraste', contrastOpts: ['Normal', 'Oscuro', 'Claro', 'Invertido'],
      saturation: 'Saturación', saturationOpts: ['Normal', 'Baja', 'Alta', 'Escala de grises'],
      links: 'Resaltar enlaces',
      reading: 'Lectura y movimiento', pause: 'Pausar animaciones', guide: 'Guía de lectura', cursor: 'Cursor grande', images: 'Ocultar imágenes',
      reset: 'Restablecer todo', statement: 'Declaración de accesibilidad',
      sizeNow: 'Tamaño del texto: {v}', resetDone: 'Se restablecieron todos los ajustes', pct: '{n}\u00a0%'
    },
    fr: {
      launcher: 'Paramètres d’accessibilité', title: 'Accessibilité',
      intro: 'Modifiez l’affichage de ce site selon vos besoins. Vos choix sont enregistrés sur cet appareil.',
      close: 'Fermer', text: 'Texte', size: 'Taille du texte', smaller: 'Réduire la taille du texte', larger: 'Augmenter la taille du texte',
      lh: 'Interligne', lhOpts: ['Normal', '1,5×', '1,75×', '2×'],
      spacing: 'Espacement du texte', font: 'Police lisible',
      color: 'Couleurs et contraste', contrast: 'Contraste', contrastOpts: ['Normal', 'Foncé', 'Clair', 'Inversé'],
      saturation: 'Saturation', saturationOpts: ['Normale', 'Faible', 'Élevée', 'Niveaux de gris'],
      links: 'Surligner les liens',
      reading: 'Lecture et mouvement', pause: 'Arrêter les animations', guide: 'Guide de lecture', cursor: 'Grand curseur', images: 'Masquer les images',
      reset: 'Tout réinitialiser', statement: 'Déclaration d’accessibilité',
      sizeNow: 'Taille du texte\u00a0: {v}', resetDone: 'Tous les réglages ont été réinitialisés', pct: '{n}\u00a0%'
    }
  };
  STR['en-au'] = merge(STR.en, { color: 'Colour and contrast', saturationOpts: ['Default', 'Low', 'High', 'Greyscale'] });
  // Strings follow the store's locale and country (data-locale="fr-CA", "en-AU"…), else <html lang>. Any other
  // language gets English, and the panel is marked lang="en" so screen readers pronounce it correctly.
  var LOCALE = ((script && script.getAttribute('data-locale')) || document.documentElement.lang || 'en').toLowerCase();
  var LANG = STR[LOCALE] || STR[LOCALE.split('-')[0]] ? LOCALE.split('-')[0] : 'en';
  var T = STR[LOCALE] || STR[LANG];
  function merge(a, b) { var o = {}, k; for (k in a) o[k] = a[k]; for (k in b) o[k] = b[k]; return o; }

  var CURSOR_ARROW = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Cpath d='M7 4v35l9.5-9 6.5 15 6.5-2.8-6.5-14.7H36z' fill='%23000' stroke='%23fff' stroke-width='3' stroke-linejoin='round'/%3E%3C/svg%3E\") 7 4, auto";
  var CURSOR_HAND = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Cpath d='M18 4.5c-2 0-3.5 1.6-3.5 3.6V27l-3.1-3.1a3.6 3.6 0 0 0-5.1 5.1l10.6 11.2A12 12 0 0 0 25.6 44H31c6 0 10.5-4.8 10.5-10.8V23.5c0-2-1.6-3.6-3.6-3.6s-3.6 1.6-3.6 3.6v-1.8c0-2-1.6-3.6-3.6-3.6s-3.6 1.6-3.6 3.6v-1.4c0-2-1.6-3.6-3.6-3.6S21.5 18.3 21.5 20.3V8.1c0-2-1.5-3.6-3.5-3.6z' fill='%23000' stroke='%23fff' stroke-width='2.5' stroke-linejoin='round'/%3E%3C/svg%3E\") 18 5, pointer";

  var root = document.documentElement;
  var prefs = {};
  var have = { text: false, color: false }, observer = null, queue = [], textQueue = [], flushTimer = 0, lastWidth = window.innerWidth;
  var host, shadow, dlg, launcher, guide, live, opener;
  var ui = { radios: {}, tiles: {} };
  var pausedByUs = [];

  /* ---------- preferences ---------- */

  function clean(p) {
    var out = {};
    if (!p || typeof p !== 'object') return out;
    var t = parseInt(p.text, 10);
    if (STEPS.indexOf(t) > 0) out.text = t;
    for (var k in CHOICES) if (CHOICES[k].indexOf(p[k]) !== -1) out[k] = p[k];
    TOGGLES.forEach(function (k) { if (p[k] === true) out[k] = true; });
    return out;
  }
  function load() {
    try { return clean(JSON.parse(localStorage.getItem(KEY) || '{}')); } catch (e) { return {}; }
  }
  function save() {
    try {
      if (isEmpty(prefs)) localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch (e) { /* private mode or storage blocked: settings last for this page only */ }
  }
  function isEmpty(o) { for (var k in o) return false; return true; }
  // Which measurements the active settings need: text sizes, or colours and backgrounds.
  function aspects() {
    return { text: !!(prefs.text || prefs.lh || prefs.spacing || prefs.font), color: !!(prefs.contrast || prefs.images) };
  }
  function needsScan() { var a = aspects(); return a.text || a.color; }

  function applyAttrs() {
    KEYS.forEach(function (k) { mark(root, A + k, prefs[k] === undefined ? null : prefs[k] === true ? true : String(prefs[k])); });
    var css = document.getElementById('tae-a11y-css'), media = isEmpty(prefs) ? 'not all' : 'screen';
    if (css && css.media !== media) css.media = media;
  }

  /* ---------- measuring ----------
     Text sizes and colours are changed from each element's ORIGINAL computed values, recorded here:
     font size and line height as inline custom properties (--tae-fs, --tae-lh), backgrounds as
     marker attributes. To read originals, our page styles must not apply to what is being read:
       - a full scan removes the data-tae-* settings from <html> (one full style recalculation);
       - new content (popups, drawers, lazy sections) is read with only its own subtree exempted
         (data-tae-x), which is much cheaper. New content inside already-scaled text inherits the
         scaled size, so that case still takes the full route.
     No paint happens in between, and transitions are paused (data-tae-measuring, data-tae-nt) so
     nothing flickers. */

  function alpha(c) {
    if (!c || c === 'transparent') return 0;
    var m = /\(([^)]*)\)/.exec(c), a;
    if (!m) return 1;
    if (m[1].indexOf('/') !== -1) a = m[1].split('/').pop(); // color(srgb r g b / a), oklch(l c h / a)
    else { var parts = m[1].split(','); if (parts.length !== 4) return 1; a = parts[3]; } // rgba(r, g, b, a)
    a = a.trim();
    return a.slice(-1) === '%' ? parseFloat(a) / 100 : parseFloat(a);
  }
  function hasOwnText(el) {
    for (var n = el.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3 && /\S/.test(n.data)) return true;
    }
    return false;
  }
  function luminance(c) {
    var m = /^rgba?\(([^)]*)\)/.exec(c || '');
    if (!m) return 1;
    var p = m[1].split(/[\s,\/]+/).filter(Boolean).slice(0, 3).map(function (v) {
      v = parseFloat(v) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * p[0] + 0.7152 * p[1] + 0.0722 * p[2];
  }
  // A small or very thin solid shape (icon line, dot, divider). In the contrast modes these keep their
  // colour and only have their lightness flipped when needed, so a black menu icon doesn't vanish on black.
  // Returns 'd' (dark shape) or 'l' (light shape), or '' if it isn't one.
  function graphic(style) {
    var w = parseFloat(style.width), h = parseFloat(style.height);
    if (!(w > 0 && h > 0)) return '';
    if ((w <= 48 && h <= 48) || Math.min(w, h) <= 4) return luminance(style.backgroundColor) < 0.35 ? 'd' : 'l';
    return '';
  }
  // ::before/::after with a solid background: 'b' for a band (recoloured black/white), or a graphic kind.
  function pseudoKind(el, which) {
    var ps = getComputedStyle(el, which);
    if (ps.content === 'none' || ps.content === 'normal' || alpha(ps.backgroundColor) < 0.6) return '';
    return graphic(ps) || 'b';
  }
  // A header or bar fixed to the screen is treated as solid even if it is transparent now: many themes only
  // give the sticky header its background once the page scrolls, and text would otherwise run over the page.
  function bar(style) {
    if (style.position !== 'fixed' && style.position !== 'sticky') return false;
    var w = parseFloat(style.width), h = parseFloat(style.height);
    return w >= window.innerWidth * 0.5 && h > 0 && h < window.innerHeight * 0.4;
  }
  function isField(el) {
    return el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' ||
      (el.tagName === 'INPUT' && !/^(hidden|image|checkbox|radio|range|color|file)$/i.test(el.type));
  }
  function scannable(el) {
    return el.nodeType === 1 && !SKIP[el.tagName] && !(el instanceof SVGElement) && el !== host;
  }
  // value: true sets an empty marker, a non-empty string sets that value, anything else removes it.
  function mark(el, name, value) {
    if (value === true) value = '';
    else if (!value) value = null;
    if (value !== null) { if (el.getAttribute(name) !== value) el.setAttribute(name, value); }
    else if (el.hasAttribute(name)) el.removeAttribute(name);
  }

  // roots: measure only these new subtrees (cheap); without roots, the whole page's styles are hidden.
  function measure(els, asp, roots) {
    var saved = {}, i, el, cs, r, rows = [];
    if (roots) {
      roots.forEach(function (n) { n.setAttribute(A + 'x', ''); n.setAttribute(A + 'nt', ''); });
      // Moved or copied content may still carry marks (and its scaled size); drop them so it is read fresh.
      els.forEach(function (n) { MARKS.forEach(function (m) { if (n.hasAttribute(A + m)) n.removeAttribute(A + m); }); });
    } else {
      MEASURED.forEach(function (k) {
        if (root.hasAttribute(A + k)) { saved[A + k] = root.getAttribute(A + k); root.removeAttribute(A + k); }
      });
      root.setAttribute(A + 'measuring', '');
    }

    // Read everything first, then write, so styles are recalculated once rather than per element.
    for (i = 0; i < els.length; i++) {
      el = els[i];
      if (!el.isConnected) continue;
      r = { el: el, text: isField(el) || hasOwnText(el) };
      cs = null;
      r.readText = asp.text && r.text;
      if (r.readText) { cs = getComputedStyle(el); r.fs = cs.fontSize; r.lh = cs.lineHeight; r.icon = ICON_FONT.test(cs.fontFamily); }
      if (asp.text && el.matches(BUTTONS)) {
        cs = cs || getComputedStyle(el);
        r.tw = el.offsetWidth >= 24 && !(parseFloat(cs.minWidth) >= 24);
        r.th = el.offsetHeight >= 24 && !(parseFloat(cs.minHeight) >= 24);
      }
      if (asp.color) {
        cs = cs || getComputedStyle(el);
        r.bg = alpha(cs.backgroundColor) >= 0.6 || bar(cs);
        r.bgi = cs.backgroundImage !== 'none';
        if (r.bg && !r.text && !/\S/.test(el.textContent)) r.g = graphic(cs);
        // Full-width colour bands and icon lines are often drawn with an absolutely positioned
        // ::before/::after, so check the pseudo-elements of positioned elements too.
        if (cs.position !== 'static') { r.pb = pseudoKind(el, '::before'); r.pa = pseudoKind(el, '::after'); }
      }
      rows.push(r);
    }

    if (roots) roots.forEach(function (n) { n.removeAttribute(A + 'x'); });
    else for (var a in saved) root.setAttribute(a, saved[a]);

    for (i = 0; i < rows.length; i++) {
      r = rows[i]; el = r.el;
      if (r.readText) {
        mark(el, A + 't', true);
        el.style.setProperty('--tae-fs', r.fs);
        if (r.lh && r.lh !== 'normal') { el.style.setProperty('--tae-lh', r.lh); mark(el, A + 'lp', true); }
        else { el.style.removeProperty('--tae-lh'); mark(el, A + 'lp', false); }
        mark(el, A + 'i', r.icon);
      } else if (asp.text && !r.text) mark(el, A + 't', false);
      if (asp.text) { mark(el, A + 'tw', r.tw); mark(el, A + 'th', r.th); }
      if (asp.color) {
        mark(el, A + 'bg', r.bg && !r.g);
        mark(el, A + 'g', r.g);
        mark(el, A + 'pb', r.pb);
        mark(el, A + 'pa', r.pa);
        mark(el, A + 'bgi', r.bgi);
        mark(el, A + 'bgt', r.bgi && /\S/.test(el.textContent));
      }
    }

    // Apply the new styles while transitions are still paused, then let transitions back in.
    if (roots) { void getComputedStyle(roots[0]).color; roots.forEach(function (n) { n.removeAttribute(A + 'nt'); }); }
    else { void getComputedStyle(root).color; root.removeAttribute(A + 'measuring'); }
  }

  function scanAll(asp) {
    if (!document.body) return;
    var all = document.body.getElementsByTagName('*'), els = [];
    for (var i = 0; i < all.length; i++) if (scannable(all[i])) els.push(all[i]);
    measure(els, asp, null);
    if (asp.text) have.text = true;
    if (asp.color) have.color = true;
  }

  // New content (cart drawer, popups, lazy sections) is measured as it arrives, in batches:
  // every 250 ms while the page is still loading (apps add a lot then), every 80 ms afterwards.
  function startObserver() {
    if (observer || !window.MutationObserver) return;
    observer = new MutationObserver(function (list) {
      var wantText = aspects().text;
      for (var i = 0; i < list.length; i++) {
        var m = list[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType === 1) { if (scannable(n)) queue.push(n); }
          else if (wantText && n.nodeType === 3 && m.target.nodeType === 1 && /\S/.test(n.data) && !m.target.hasAttribute(A + 't')) textQueue.push(m.target);
        }
      }
      if ((queue.length || textQueue.length) && !flushTimer) flushTimer = setTimeout(flush, document.readyState === 'complete' ? 80 : 250);
    });
    observer.observe(root, { childList: true, subtree: true });
  }
  function stopObserver() {
    if (observer) { observer.disconnect(); observer = null; }
    queue = []; textQueue = [];
    clearTimeout(flushTimer); flushTimer = 0;
  }
  function flush() {
    flushTimer = 0;
    var queued = queue, texts = textQueue, asp = aspects(), roots = [], els = [], i, j;
    var seen = typeof Set === 'function' ? new Set() : null;
    queue = []; textQueue = [];
    if (!document.body || !(asp.text || asp.color)) return;
    function add(el) {
      if (!scannable(el)) return false;
      if (seen) { if (seen.has(el)) return false; seen.add(el); }
      els.push(el);
      return true;
    }
    for (i = 0; i < queued.length; i++) if (queued[i].isConnected && document.body.contains(queued[i])) roots.push(queued[i]);
    for (i = 0; i < texts.length; i++) if (texts[i].isConnected && !texts[i].hasAttribute(A + 't') && document.body.contains(texts[i])) roots.push(texts[i]);
    // New content inside already-scaled text inherits the scaled size, so re-read from the outermost scaled
    // ancestor: only that part of the page is recalculated, and nothing else has its transitions paused.
    if (asp.text) {
      roots = roots.map(function (n) {
        var top = n;
        for (var p = n.parentElement; p && p !== document.body; p = p.parentElement) if (p.hasAttribute(A + 't')) top = p;
        return top;
      });
    }
    // Keep only outermost roots, then read each root and everything inside it.
    var rootSet = typeof Set === 'function' ? new Set(roots) : null, tops = [];
    for (i = 0; i < roots.length; i++) {
      var n = roots[i], nested = false;
      for (var a = n.parentElement; a && !nested; a = a.parentElement) nested = rootSet ? rootSet.has(a) : roots.indexOf(a) !== -1;
      if (!nested && tops.indexOf(n) === -1) tops.push(n);
    }
    for (i = 0; i < tops.length; i++) {
      add(tops[i]);
      var all = tops[i].getElementsByTagName('*');
      for (j = 0; j < all.length; j++) add(all[j]);
    }
    if (!els.length || !tops.length) return;
    measure(els, asp, tops);
    if (host) enhanceTriggers();
  }

  // Lazy-loaded background images (lazysizes, used by the theme) arrive after the scan: re-read them.
  document.addEventListener('lazyloaded', function (e) {
    if (!observer || !e.target || e.target.nodeType !== 1) return;
    queue.push(e.target);
    if (!flushTimer) flushTimer = setTimeout(flush, 80);
  });

  /* ---------- pause videos and sliders ---------- */

  // UPDATED 20.10.2026 phase 2 (WCAG 2.2.2): autoplaying Vimeo frames (and YouTube frames that accept commands) are
  // paused through the players' own message interface and started again when the setting is switched off. Landing
  // pages use looping Vimeo banners with no controls, which nothing else can pause.
  var FRAME_SEL = 'iframe[src*="player.vimeo.com/video/"], iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]';
  function frameAutoplays(f) { return /[?&](autoplay|background)=1/.test(f.getAttribute('src') || ''); }
  function tellFrame(f, play) {
    var src = f.getAttribute('src') || '', w = f.contentWindow;
    if (!w) return;
    try {
      if (/player\.vimeo\.com/.test(src)) w.postMessage(JSON.stringify({ method: play ? 'play' : 'pause' }), 'https://player.vimeo.com');
      else if (/[?&]enablejsapi=1/.test(src)) w.postMessage(JSON.stringify({ event: 'command', func: play ? 'playVideo' : 'pauseVideo', args: [] }), '*');
    } catch (e) {}
  }
  function pauseFrames(on) {
    each(FRAME_SEL, function (f) {
      if (on) {
        if (frameAutoplays(f)) { tellFrame(f, false); f.setAttribute(A + 'paused', ''); }
      } else if (f.hasAttribute(A + 'paused')) {
        f.removeAttribute(A + 'paused');
        tellFrame(f, true);
      }
    });
  }
  // A player that loads after the setting is on is paused as soon as it says it is ready, or when its frame loads.
  window.addEventListener('message', function (e) {
    if (!prefs || !prefs.pause || e.origin !== 'https://player.vimeo.com') return;
    var d;
    try { d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; } catch (x) { return; }
    if (!d || d.event !== 'ready') return;
    each(FRAME_SEL, function (f) {
      if (f.contentWindow === e.source && frameAutoplays(f)) { tellFrame(f, false); f.setAttribute(A + 'paused', ''); }
    });
  });
  document.addEventListener('load', function (e) {
    var f = e.target;
    if (!prefs || !prefs.pause || !f || f.tagName !== 'IFRAME' || !f.matches(FRAME_SEL) || !frameAutoplays(f)) return;
    [600, 2500].forEach(function (ms) { setTimeout(function () { if (prefs.pause) { tellFrame(f, false); f.setAttribute(A + 'paused', ''); } }, ms); });
  }, true);

  function pauseMedia(on) {
    pauseFrames(on);
    var vids = document.getElementsByTagName('video'), i, v;
    for (i = 0; i < vids.length; i++) {
      v = vids[i];
      if (on) {
        if (!v.paused && (v.autoplay || v.muted || v.loop)) { v.pause(); v.setAttribute(A + 'paused', ''); }
      } else if (v.hasAttribute(A + 'paused')) {
        v.removeAttribute(A + 'paused');
        try { var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
      }
    }
    if (on) {
      try {
        if (window.Flickity && window.Flickity.data) {
          each('.flickity-enabled', function (el) {
            var f = window.Flickity.data(el);
            if (f && f.options && f.options.autoPlay && f.player && f.player.state !== 'stopped') { f.stopPlayer(); pausedByUs.push(function () { f.playPlayer(); }); }
          });
        }
      } catch (e) {}
      try {
        each('.swiper-initialized, .swiper-container-initialized', function (el) {
          var s = el.swiper;
          if (s && s.autoplay && s.autoplay.running) { s.autoplay.stop(); pausedByUs.push(function () { s.autoplay.start(); }); }
        });
      } catch (e) {}
      try {
        var $ = window.jQuery;
        if ($ && $.fn && $.fn.slick) {
          $('.slick-initialized').each(function () {
            var $s = $(this);
            if ($s.slick('slickGetOption', 'autoplay')) { $s.slick('slickPause'); pausedByUs.push(function () { $s.slick('slickPlay'); }); }
          });
        }
      } catch (e) {}
    } else {
      pausedByUs.splice(0).forEach(function (resume) { try { resume(); } catch (e) {} });
    }
  }
  function each(sel, fn) { Array.prototype.forEach.call(document.querySelectorAll(sel), fn); }

  // Autoplay videos that start later (lazy-loaded) are paused too; videos the visitor starts are left alone.
  document.addEventListener('play', function (e) {
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO') return;
    var byUser = navigator.userActivation ? navigator.userActivation.isActive : !!v.controls;
    if (byUser) { v.removeAttribute(A + 'paused'); return; } // the visitor is in charge of this video now
    if (prefs.pause && (v.autoplay || v.muted)) { v.pause(); v.setAttribute(A + 'paused', ''); }
  }, true);
  document.addEventListener('pause', function (e) {
    var v = e.target;
    if (v && v.tagName === 'VIDEO' && navigator.userActivation && navigator.userActivation.isActive) v.removeAttribute(A + 'paused');
  }, true);

  /* ---------- reading guide ---------- */

  var guideY = 0, guideFrame = 0;
  function moveGuide(e) {
    if (e.pointerType === 'touch') return;
    guideY = e.clientY;
    if (!guideFrame) guideFrame = requestAnimationFrame(function () {
      guideFrame = 0;
      guide.style.transform = 'translate3d(0,' + guideY + 'px,0)';
    });
  }
  function setGuide(on) {
    if (!guide) return;
    on = on && FINE_POINTER;
    guide.hidden = !on;
    if (on) window.addEventListener('pointermove', moveGuide, { passive: true });
    else window.removeEventListener('pointermove', moveGuide, { passive: true });
  }

  /* ---------- floating button placement ----------
     If something fixed already sits in the button's corner (the sticky "Add to cart" bar on product
     pages, the Smile.io loyalty button, a popup teaser), stack the button above it. Full-screen popups
     and drawers are ignored. Re-checked on scroll and while late widgets load. */

  var placeTimer = 0, placeFrame = 0;
  function placeLauncher() {
    if (!launcher) return;
    var vh = window.innerHeight, lift = 0;
    var x = host.getAttribute('data-pos') === 'right' ? window.innerWidth - 40 : 40;
    var hits = document.elementsFromPoint ? document.elementsFromPoint(x, vh - 40) : [];
    for (var i = 0; i < hits.length; i++) {
      var el = hits[i];
      if (el === host) continue;
      for (var e = el; e && e !== document.body && e !== root; e = e.parentElement) {
        var pos = getComputedStyle(e).position;
        if (pos === 'fixed' || pos === 'sticky') {
          var b = e.getBoundingClientRect();
          if (b.height > 0 && b.height < vh * 0.35 && b.bottom > vh * 0.6) lift = vh - b.top;
          break;
        }
      }
      break; // only the topmost page element at that spot matters
    }
    launcher.style.bottom = 'calc(' + (lift ? Math.round(lift) + 12 : 16) + 'px + env(safe-area-inset-bottom, 0px))';
  }
  // Runs once scrolling has settled, inside an animation frame, so it never forces extra layout mid-scroll.
  function schedulePlace() {
    clearTimeout(placeTimer);
    placeTimer = setTimeout(function () {
      if (!placeFrame) placeFrame = requestAnimationFrame(function () { placeFrame = 0; placeLauncher(); });
    }, 150);
  }

  /* ---------- applying changes ---------- */

  function commit(changed, fromStorage) {
    if (!fromStorage) save();
    var asp = aspects(), missing = { text: asp.text && !have.text, color: asp.color && !have.color };
    if (missing.text || missing.color) scanAll(missing);
    if (!asp.text) have.text = false; // stale once switched off; re-measured if switched on again
    if (!asp.color) have.color = false;
    if (asp.text || asp.color) startObserver(); else stopObserver();
    applyAttrs();
    syncHost();
    if (changed === 'pause' || changed === '*') pauseMedia(!!prefs.pause);
    if (changed === 'guide' || changed === '*') setGuide(!!prefs.guide);
    // Sliders and sticky headers size themselves in JS; a resize event makes them re-measure the new text.
    if (/^(text|lh|spacing|font|\*)$/.test(changed)) {
      clearTimeout(commit.nudge);
      commit.nudge = setTimeout(function () { try { window.dispatchEvent(new Event('resize')); } catch (e) {} }, 80);
    }
    render();
    try { window.dispatchEvent(new CustomEvent('tae-a11y:change', { detail: JSON.parse(JSON.stringify(prefs)) })); } catch (e) {}
  }
  function set(k, v) {
    if (v === undefined || v === null || v === false || v === '' || (k === 'text' && v === 100)) delete prefs[k];
    else prefs[k] = v;
    commit(k);
  }
  function stepText(d) {
    var i = STEPS.indexOf(prefs.text || 100) + d;
    if (i < 0 || i >= STEPS.length) return;
    set('text', STEPS[i]);
    announce(T.sizeNow.replace('{v}', pct(STEPS[i])));
  }
  function reset() {
    prefs = {};
    commit('*');
    announce(T.resetDone);
  }
  function pct(n) { return T.pct.replace('{n}', n); }

  /* ---------- UI ---------- */

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function icon(paths, extra) {
    return '<svg class="ic" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' + (extra || '') + '>' + paths + '</svg>';
  }
  var ICON = {
    access: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10.6" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="6.4" r="1.75" fill="currentColor"/><path d="M6.3 9.2c1.9.55 3.8.85 5.7.85s3.8-.3 5.7-.85M12 10.1v3.6m0 0-2.5 5.4m2.5-5.4 2.5 5.4" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    close: icon('<path d="M6 6l12 12M18 6 6 18"/>'),
    minus: icon('<path d="M5 12h14"/>', ' stroke-width="2.6"'),
    plus: icon('<path d="M12 5v14M5 12h14"/>', ' stroke-width="2.6"'),
    check: icon('<path d="M5 12.5l4.5 4.5L19 7"/>', ' stroke-width="3"'),
    reset: icon('<path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.5 8.5"/><path d="M3.5 3.5v5h5"/>'),
    spacing: icon('<path d="M2.5 17 6 7l3.5 10M3.6 14h4.8"/><path d="M14 12h7.5M16.5 9.5 14 12l2.5 2.5M19 9.5l2.5 2.5-2.5 2.5"/>'),
    font: icon('<path d="M2.5 18 7 6l4.5 12M4.2 14h5.6"/><circle cx="17" cy="14.5" r="3.2"/><path d="M20.2 11v7"/>'),
    links: icon('<path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.2 1.2"/><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.2-1.2"/>'),
    pause: icon('<rect x="6.5" y="5" width="3.6" height="14" rx="1"/><rect x="13.9" y="5" width="3.6" height="14" rx="1"/>'),
    guide: icon('<path d="M4 5.5h16M4 18.5h16"/><rect x="2.5" y="10" width="19" height="4" rx="1"/>'),
    cursor: icon('<path d="M5.5 3.5 18.5 10l-5.6 1.9-2.2 5.6z"/><path d="m13 12 5 5"/>'),
    images: icon('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 15 5-4.5 4 3.5 3-2.5 6 5"/><path d="M3.5 3.5l17 17"/>')
  };

  function seg(key, legend, labels, values, decor) {
    var h = '<fieldset><legend>' + esc(legend) + '</legend><div class="seg">';
    for (var i = 0; i < values.length; i++) {
      h += '<label><input type="radio" name="tae-' + key + '" value="' + values[i] + '" data-key="' + key + '">' +
        '<span>' + (decor ? decor(values[i]) : '') + '<span class="opt">' + esc(labels[i]) + '</span></span></label>';
    }
    return h + '</div></fieldset>';
  }
  function tile(key, ic) {
    return '<button type="button" class="tile" data-key="' + key + '" aria-pressed="false">' + ic +
      '<span class="lbl">' + esc(T[key]) + '</span><span class="chk" aria-hidden="true">' + ICON.check + '</span></button>';
  }
  function contrastSwatch(v) { return '<b class="sw sw-' + (v || 'default') + '" aria-hidden="true">' + (v === 'invert' ? '' : 'Aa') + '</b>'; }
  function saturationSwatch(v) { return '<b class="dots dots-' + (v || 'default') + '" aria-hidden="true"><i></i><i></i><i></i></b>'; }

  function statementUrl() {
    var custom = script && script.getAttribute('data-statement');
    if (custom === 'none') return ''; // this store has no accessibility statement page
    if (custom) return custom;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + 'pages/accessibility-statement';
  }

  function buildHost() {
    host = document.createElement('tae-a11y');
    var pos = (script && script.getAttribute('data-launcher')) || 'none';
    host.setAttribute('data-pos', pos);
    host.setAttribute('lang', LANG);
    shadow = host.attachShadow ? host.attachShadow({ mode: 'open' }) : null;
    if (!shadow) return false;
    shadow.innerHTML = '<style>' + CSS + '</style>' +
      (pos === 'none' ? '' : '<button type="button" class="launcher" aria-haspopup="dialog" aria-label="' + esc(T.launcher) + '">' + ICON.access + '</button>') +
      '<div class="guide" aria-hidden="true" hidden></div>';
    launcher = shadow.querySelector('.launcher');
    guide = shadow.querySelector('.guide');
    if (launcher) launcher.addEventListener('click', function () { open(launcher); });
    document.body.appendChild(host);
    return true;
  }

  function buildPanel() {
    var wrap = document.createElement('div');
    wrap.innerHTML =
      '<dialog class="panel" aria-labelledby="tae-title" aria-describedby="tae-intro">' +
        '<div class="head">' +
          '<div class="titles"><h2 id="tae-title" tabindex="-1">' + esc(T.title) + '</h2><p id="tae-intro">' + esc(T.intro) + '</p></div>' +
          '<button type="button" class="close" data-act="close" aria-label="' + esc(T.close) + '">' + ICON.close + '</button>' +
        '</div>' +
        '<div class="body">' +
          '<section><h3>' + esc(T.text) + '</h3>' +
            '<div class="row" role="group" aria-labelledby="tae-size-label">' +
              '<span class="label" id="tae-size-label">' + esc(T.size) + '</span>' +
              '<span class="stepper">' +
                '<button type="button" class="step" data-act="smaller" aria-label="' + esc(T.smaller) + '" aria-describedby="tae-size-value">' + ICON.minus + '</button>' +
                '<span class="value" id="tae-size-value">' + esc(pct(100)) + '</span>' +
                '<button type="button" class="step" data-act="larger" aria-label="' + esc(T.larger) + '" aria-describedby="tae-size-value">' + ICON.plus + '</button>' +
              '</span>' +
            '</div>' +
            '<div class="meter" aria-hidden="true">' + STEPS.slice(1).map(function () { return '<i></i>'; }).join('') + '</div>' +
            seg('lh', T.lh, T.lhOpts, [''].concat(CHOICES.lh)) +
            '<div class="tiles">' + tile('spacing', ICON.spacing) + tile('font', ICON.font) + '</div>' +
          '</section>' +
          '<section><h3>' + esc(T.color) + '</h3>' +
            seg('contrast', T.contrast, T.contrastOpts, [''].concat(CHOICES.contrast), contrastSwatch) +
            seg('saturation', T.saturation, T.saturationOpts, [''].concat(CHOICES.saturation), saturationSwatch) +
            '<div class="tiles">' + tile('links', ICON.links) + '</div>' +
          '</section>' +
          '<section><h3>' + esc(T.reading) + '</h3>' +
            '<div class="tiles">' + tile('pause', ICON.pause) +
              (FINE_POINTER ? tile('guide', ICON.guide) + tile('cursor', ICON.cursor) : '') +
              tile('images', ICON.images) + '</div>' +
          '</section>' +
        '</div>' +
        '<div class="foot">' +
          '<button type="button" class="reset" data-act="reset">' + ICON.reset + '<span>' + esc(T.reset) + '</span></button>' +
          (statementUrl() ? '<a href="' + esc(statementUrl()) + '">' + esc(T.statement) + '</a>' : '') +
        '</div>' +
        '<div class="sr" role="status" aria-live="polite" aria-atomic="true"></div>' +
      '</dialog>';
    dlg = wrap.firstChild;
    shadow.appendChild(dlg);
    live = dlg.querySelector('.sr');
    ui.title = dlg.querySelector('#tae-title');
    ui.value = dlg.querySelector('.value');
    ui.smaller = dlg.querySelector('[data-act="smaller"]');
    ui.larger = dlg.querySelector('[data-act="larger"]');
    ui.meter = dlg.querySelectorAll('.meter i');
    ui.reset = dlg.querySelector('[data-act="reset"]');
    Array.prototype.forEach.call(dlg.querySelectorAll('input[type="radio"]'), function (r) {
      var k = r.getAttribute('data-key');
      (ui.radios[k] = ui.radios[k] || []).push(r);
    });
    Array.prototype.forEach.call(dlg.querySelectorAll('.tile'), function (b) { ui.tiles[b.getAttribute('data-key')] = b; });

    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) { // click on the backdrop
        var r = dlg.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
        return;
      }
      var b = e.target.closest('button');
      if (!b || b.getAttribute('aria-disabled') === 'true') return;
      var act = b.getAttribute('data-act');
      if (act === 'close') close();
      else if (act === 'smaller') stepText(-1);
      else if (act === 'larger') stepText(1);
      else if (act === 'reset') reset();
      else if (b.classList.contains('tile')) { var k = b.getAttribute('data-key'); set(k, !prefs[k]); }
    });
    dlg.addEventListener('change', function (e) {
      var r = e.target;
      if (r.type === 'radio' && r.checked) set(r.getAttribute('data-key'), r.value || undefined);
    });
    dlg.addEventListener('close', function () {
      // Back to the link that opened the panel; if it has been hidden meanwhile (a closed mobile menu),
      // to that menu's button, then any visible "Accessibility" link, then the floating button.
      var from = opener, menu = from && from.closest && from.closest('details');
      var options = [from, menu && menu.querySelector('summary'), visibleTrigger(), launcher];
      opener = null;
      for (var i = 0; i < options.length; i++) {
        var el = options[i];
        if (!el || !el.isConnected || !el.getClientRects().length) continue;
        try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
        if (el.matches(':focus')) break;
      }
    });
  }

  function render() {
    if (!dlg) return;
    var t = prefs.text || 100, i = STEPS.indexOf(t);
    ui.value.textContent = pct(t);
    ui.smaller.setAttribute('aria-disabled', i <= 0 ? 'true' : 'false');
    ui.larger.setAttribute('aria-disabled', i >= STEPS.length - 1 ? 'true' : 'false');
    Array.prototype.forEach.call(ui.meter, function (m, j) { m.className = j < i ? 'on' : ''; });
    for (var k in ui.radios) {
      var v = prefs[k] || '';
      ui.radios[k].forEach(function (r) { r.checked = r.value === v; });
    }
    for (var key in ui.tiles) ui.tiles[key].setAttribute('aria-pressed', prefs[key] ? 'true' : 'false');
    ui.reset.setAttribute('aria-disabled', isEmpty(prefs) ? 'true' : 'false');
  }
  function syncHost() {
    if (!host) return;
    ['contrast', 'pause', 'cursor', 'font'].forEach(function (k) {
      if (prefs[k]) host.setAttribute('data-' + k, prefs[k] === true ? '' : prefs[k]);
      else host.removeAttribute('data-' + k);
    });
  }
  function announce(msg) {
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 60);
  }

  function open(from) {
    if (!ensureHost()) return;
    if (!dlg) buildPanel();
    if (dlg.open) return;
    opener = from && from.focus ? from : document.activeElement;
    if (!host.isConnected) document.body.appendChild(host); // another script removed it
    render();
    dlg.querySelector('.body').scrollTop = 0;
    dlg.showModal();
    ui.title.focus();
  }
  function close() {
    if (dlg && dlg.open) dlg.close();
  }

  function visibleTrigger() {
    var all = document.querySelectorAll(TRIGGERS);
    for (var i = 0; i < all.length; i++) if (all[i].getClientRects().length) return all[i];
    return null;
  }

  // The site's existing "Accessibility" links (id="Accessibility") become buttons that open the panel.
  function enhanceTriggers() {
    each(TRIGGERS, function (el) {
      if (el.hasAttribute(A + 'trigger')) return;
      el.setAttribute(A + 'trigger', '');
      if (el.tagName !== 'BUTTON') el.setAttribute('role', 'button');
      el.setAttribute('aria-haspopup', 'dialog');
    });
  }
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest(TRIGGERS);
    if (!t) return;
    e.preventDefault();
    open(t);
  }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar') return;
    var t = e.target && e.target.closest && e.target.closest(TRIGGERS);
    if (!t || t.tagName === 'BUTTON') return;
    e.preventDefault();
    open(t);
  }, true);

  /* ---------- panel styles (shadow DOM, so the theme's CSS cannot leak in) ---------- */

  var CSS = [
    ':host{all:initial !important;--s:min(var(--tae-scale,1),1.4)}',
    '*,*::before,*::after{box-sizing:border-box}',
    '[hidden]{display:none !important}',
    '.launcher{position:fixed;z-index:900;left:16px;bottom:calc(16px + env(safe-area-inset-bottom,0px));width:48px;height:48px;padding:0;margin:0;border:2px solid #fff;border-radius:50%;background:#165028;color:#fff;display:grid;place-items:center;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,.3)}',
    ':host([data-pos="right"]) .launcher{left:auto;right:16px}',
    '.launcher svg{width:32px;height:32px;display:block}',
    '@media (max-width:640px){.launcher{width:44px;height:44px}.launcher svg{width:30px;height:30px}}',
    '.launcher:hover{background:#0e3a1c}',
    '.launcher:focus-visible{outline:3px solid #fff;outline-offset:2px;box-shadow:0 0 0 7px #165028}',
    // The guide's clear band is taller than a line of text, and grows with the text size setting.
    '.guide{position:fixed;left:0;right:0;font-size:16px;top:calc(-1.25em * var(--tae-scale,1));height:calc(2.5em * var(--tae-scale,1));z-index:2147483600;pointer-events:none;border-top:2px solid #111;border-bottom:2px solid #111;background:rgba(255,214,10,.22);box-shadow:0 -1px 0 #fff,0 1px 0 #fff;will-change:transform}',

    '.panel{--bg:#fff;--ink:#1f2421;--muted:#505a54;--brand:#165028;--on-brand:#fff;--tile:#f7f5ef;--tile-hover:#efece3;--line:#76817a;--soft:#e4e1d8;--focus:#165028;',
    'position:fixed;inset:0 0 0 auto;margin:0;padding:0;border:0;width:min(26em,100vw);max-width:100vw;height:100%;max-height:100%;',
    'background:var(--bg);color:var(--ink);font:400 calc(16px * var(--s,1))/1.5 "Open Sans",system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;box-shadow:-10px 0 40px rgba(0,0,0,.2);overflow:hidden}',
    '.panel[open]{display:flex;flex-direction:column;animation:tae-in-right .22s ease-out}',
    '.panel::backdrop{background:rgba(16,28,20,.3)}',
    ':host([data-contrast="dark"]) .panel{--bg:#000;--ink:#fff;--muted:#e6e6e6;--brand:#ffe94d;--on-brand:#000;--tile:#000;--tile-hover:#1a1a1a;--line:#fff;--soft:#666;--focus:#ffe94d}',
    ':host([data-contrast="light"]) .panel{--ink:#000;--muted:#000;--brand:#000;--tile:#fff;--tile-hover:#f2f2f2;--line:#000;--soft:#000;--focus:#0030b8}',
    ':host([data-font]) .panel,:host([data-font]) .panel h2{font-family:"Atkinson Hyperlegible Next","Atkinson Hyperlegible",Verdana,Tahoma,"Segoe UI",Arial,sans-serif;font-style:normal}',

    '.head{display:flex;align-items:flex-start;gap:.75em;padding:1.25em 1em 1em 1.5em;border-bottom:1px solid var(--soft)}',
    '.titles{flex:1;min-width:0}',
    'h2{margin:0;font:italic 400 1.625em/1.2 Merriweather,Georgia,serif;color:var(--brand)}',
    'h2:focus{outline:none}', // focused on open so screen readers start at the title
    'h2:focus-visible{outline:2px solid var(--focus);outline-offset:4px;border-radius:2px}',
    '.head p{margin:.4em 0 0;font-size:.875em;color:var(--muted)}',
    '.close{flex:none;width:2.75em;height:2.75em;margin:-.25em 0 0;padding:0;border:0;border-radius:50%;background:transparent;color:var(--ink);display:grid;place-items:center;cursor:pointer}',
    '.close:hover{background:var(--tile-hover)}',
    '.close .ic{width:1.35em;height:1.35em}',

    '.body{flex:1 1 auto;overflow-y:auto;overscroll-behavior:contain;padding:.25em 1.5em 1.25em}',
    'section{padding:1em 0 .3em}',
    'section+section{border-top:1px solid var(--soft)}',
    'h3{margin:0 0 .8em;font-size:1.0625em;font-weight:700;color:var(--ink)}',
    '.row{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:.5em 1em;margin-bottom:.6em}',
    '.label,legend{font-weight:600}',
    '.stepper{display:flex;align-items:center;gap:.4em}',
    '.step{width:2.75em;height:2.75em;padding:0;margin:0;border:2px solid var(--brand);border-radius:50%;background:var(--bg);color:var(--brand);display:grid;place-items:center;cursor:pointer}',
    '.step .ic{width:1.15em;height:1.15em}',
    '.step:hover{background:var(--tile-hover)}',
    '.step[aria-disabled="true"]{border-color:#b9c0bb;color:#7d857f;background:var(--bg);cursor:not-allowed}',
    '.value{min-width:3.75em;text-align:center;font-weight:700;font-size:1.0625em;font-variant-numeric:tabular-nums}',
    '.meter{display:flex;gap:4px;margin:0 0 1.2em}',
    '.meter i{flex:1;height:5px;border-radius:3px;background:var(--soft)}',
    '.meter i.on{background:var(--brand)}',

    'fieldset{border:0;margin:0 0 1.1em;padding:0;min-width:0}',
    'legend{padding:0;margin:0 0 .5em}',
    '.seg{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.4em}',
    '.seg label{position:relative;display:block}',
    '.seg input{position:absolute;inset:0;width:100%;height:100%;margin:0;opacity:0;cursor:pointer}',
    '.seg label>span{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.35em;height:100%;min-height:2.75em;padding:.5em .2em;border:1px solid var(--line);border-radius:.6em;background:var(--bg);font-size:.875em;font-weight:600;line-height:1.2;text-align:center}',
    '.seg input:hover+span{border-color:var(--brand);background:var(--tile-hover)}',
    '.seg input:checked+span{background:var(--brand);border-color:var(--brand);color:var(--on-brand)}',
    '.seg input:focus-visible+span{outline:3px solid var(--focus);outline-offset:2px}',
    '.opt{overflow-wrap:anywhere}',
    '.sw{display:grid;place-items:center;width:2.6em;height:1.8em;border-radius:.35em;border:1px solid var(--line);font:700 1em/1 "Open Sans",Arial,sans-serif}',
    '.sw-default{background:#fff;color:#165028}',
    '.sw-dark{background:#000;color:#ffe94d;border-color:#fff;box-shadow:0 0 0 1px #000}',
    '.sw-light{background:#fff;color:#000;border:2px solid #000}',
    '.sw-invert{background:linear-gradient(90deg,#fff 0 50%,#000 50% 100%);border-color:var(--ink)}',
    '.dots{display:flex;gap:3px;height:1.8em;align-items:center}',
    '.dots i{width:.8em;height:.8em;border-radius:50%;border:1px solid rgba(0,0,0,.25)}',
    '.dots-default i:nth-child(1){background:#d9452b}.dots-default i:nth-child(2){background:#2e9a3b}.dots-default i:nth-child(3){background:#2f6fd6}',
    '.dots-low i:nth-child(1){background:#b9877e}.dots-low i:nth-child(2){background:#7c9a7f}.dots-low i:nth-child(3){background:#8195b6}',
    '.dots-high i:nth-child(1){background:#ff2a00}.dots-high i:nth-child(2){background:#00c21a}.dots-high i:nth-child(3){background:#0059ff}',
    '.dots-mono i:nth-child(1){background:#6b6b6b}.dots-mono i:nth-child(2){background:#9a9a9a}.dots-mono i:nth-child(3){background:#585858}',

    '.tiles{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.6em;margin:0 0 .8em}',
    '.tile{position:relative;display:flex;flex-direction:column;align-items:flex-start;justify-content:space-between;gap:.55em;min-height:4.75em;margin:0;padding:.75em 2.2em .75em .9em;border:1px solid var(--line);border-radius:.75em;background:var(--tile);color:var(--ink);font:inherit;font-size:.9375em;font-weight:600;line-height:1.25;text-align:left;cursor:pointer}',
    '.tile .ic{width:1.6em;height:1.6em;color:var(--brand)}',
    '.tile .lbl{overflow-wrap:anywhere;hyphens:auto}',
    '.tile:hover{border-color:var(--brand);background:var(--tile-hover)}',
    '.tile[aria-pressed="true"]{background:var(--brand);border-color:var(--brand);color:var(--on-brand)}',
    '.tile[aria-pressed="true"] .ic{color:var(--on-brand)}',
    '.chk{position:absolute;top:.6em;right:.6em;width:1.4em;height:1.4em;border:1.5px solid var(--line);border-radius:50%;background:var(--bg);display:grid;place-items:center}',
    '.chk .ic{width:.95em;height:.95em;color:var(--brand);opacity:0}',
    '.tile[aria-pressed="true"] .chk{border-color:var(--on-brand)}',
    '.tile[aria-pressed="true"] .chk .ic{opacity:1;color:var(--brand)}',

    '.foot{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:.75em 1em;padding:.9em 1.5em calc(.9em + env(safe-area-inset-bottom,0px));border-top:1px solid var(--soft);background:var(--bg)}',
    '.reset{display:inline-flex;align-items:center;gap:.5em;min-height:2.75em;margin:0;padding:0 1.15em;border:2px solid var(--brand);border-radius:999px;background:var(--bg);color:var(--brand);font:inherit;font-weight:700;cursor:pointer}',
    '.reset .ic{width:1.15em;height:1.15em}',
    '.reset:hover{background:var(--tile-hover)}',
    '.reset[aria-disabled="true"]{border-color:#b9c0bb;color:#7d857f;cursor:default;background:var(--bg)}',
    '.foot a{color:var(--brand);font-weight:600;text-decoration:underline;text-underline-offset:.2em}',
    '.foot a:hover{text-decoration-thickness:2px}',
    'button:focus-visible,a:focus-visible{outline:3px solid var(--focus);outline-offset:2px}',
    '.sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0}',

    '@media (max-width:640px){',
    '.panel{inset:auto 0 0 0;width:100%;height:auto;max-height:88vh;max-height:88dvh;border-radius:18px 18px 0 0;box-shadow:0 -10px 40px rgba(0,0,0,.22)}',
    '.panel[open]{animation-name:tae-in-up}',
    '.head{padding:1.1em 1em .9em 1.25em}',
    '.body{padding:.25em 1.25em 1em}',
    '.foot{padding-left:1.25em;padding-right:1.25em}',
    '}',
    '@media (max-width:360px){.seg{grid-template-columns:repeat(2,minmax(0,1fr))}}',
    // Short screens (and 400% zoom): scroll the whole panel so the settings aren't squeezed between header and footer.
    '@media (max-height:32em){.panel[open]{overflow-y:auto}.body{flex:none;overflow:visible}}',
    '@keyframes tae-in-right{from{transform:translateX(28px);opacity:0}}',
    '@keyframes tae-in-up{from{transform:translateY(28px);opacity:0}}',
    '@media (prefers-reduced-motion:reduce){.panel[open]{animation:none}}',
    ':host([data-pause]) .panel[open]{animation:none}',
    ':host([data-cursor]) *{cursor:' + CURSOR_ARROW + ' !important}',
    ':host([data-cursor]) :is(button,a,label,input){cursor:' + CURSOR_HAND + ' !important}',
    '@media print{.launcher,.guide{display:none !important}}',
    // Windows high contrast replaces our colours, so selected and pressed states use system colours.
    '@media (forced-colors:active){',
    '.panel{border:2px solid CanvasText}',
    '.seg input:checked+span,.tile[aria-pressed="true"],.meter i.on{forced-color-adjust:none;background:Highlight;border-color:Highlight;color:HighlightText}',
    '.tile[aria-pressed="true"] .ic{color:HighlightText}',
    '.tile[aria-pressed="true"] .chk{forced-color-adjust:none;background:HighlightText;border-color:HighlightText}',
    '.tile[aria-pressed="true"] .chk .ic{color:Highlight}',
    '.meter i{forced-color-adjust:none;background:GrayText}',
    '.step[aria-disabled="true"],.reset[aria-disabled="true"]{color:GrayText;border-color:GrayText}',
    '.sw,.dots i{forced-color-adjust:none}',
    'button:focus-visible,a:focus-visible,.seg input:focus-visible+span{outline:3px solid CanvasText;outline-offset:2px}',
    '}'
  ].join('\n');

  /* ---------- start ---------- */

  // The floating button, reading guide and panel aren't needed to show the page, so they are built
  // when the browser is idle (or straight away if someone opens the panel first).
  function ensureHost() {
    if (host) return !!shadow;
    if (!document.body || !buildHost()) return false;
    syncHost();
    setGuide(!!prefs.guide);
    enhanceTriggers();
    if (launcher) {
      window.addEventListener('scroll', schedulePlace, { passive: true });
      window.addEventListener('resize', schedulePlace);
      schedulePlace();
      [3000, 8000, 15000].forEach(function (t) { setTimeout(schedulePlace, t); }); // widgets that load late
    }
    return true;
  }
  function whenIdle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 3000 });
    else setTimeout(fn, 300);
  }

  function init() {
    if (!document.body) return;
    prefs = load();
    if (needsScan()) { scanAll(aspects()); startObserver(); }
    applyAttrs();
    whenIdle(ensureHost);

    if (prefs.pause) {
      pauseMedia(true);
      // Theme sliders start after this script runs; catch them once they have.
      window.addEventListener('load', function () {
        if (prefs.pause) pauseMedia(true);
        setTimeout(function () { if (prefs.pause) pauseMedia(true); }, 2500);
      });
    }
    window.addEventListener('load', function () { if (host) { enhanceTriggers(); schedulePlace(); } });

    window.addEventListener('resize', function () {
      clearTimeout(init.resizeTimer);
      init.resizeTimer = setTimeout(function () {
        if (window.innerWidth === lastWidth) return; // mobile toolbars change only the height
        lastWidth = window.innerWidth;
        if (needsScan()) scanAll(aspects()); // breakpoints change the original sizes
      }, 250);
    });

    // Keep tabs in sync, and re-attach the panel if the page swaps out <body>.
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) { prefs = load(); commit('*', true); }
    });
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (host && document.body && !host.isConnected) document.body.appendChild(host);
      }).observe(root, { childList: true });
    }

    // Someone clicked "Accessibility" while this script was still loading.
    if (window.taeA11yPending) { var pending = window.taeA11yPending; window.taeA11yPending = null; open(pending); }

    window.TAEA11y = {
      version: '1.0.0',
      open: function () { open(document.activeElement); },
      close: close,
      reset: reset,
      get: function () { return JSON.parse(JSON.stringify(prefs)); },
      // e.g. TAEA11y.set('text', 150), TAEA11y.set('contrast', 'dark'), TAEA11y.set('links', true)
      set: function (k, v) {
        var next = JSON.parse(JSON.stringify(prefs));
        next[k] = v;
        next = clean(next);
        if (next[k] === undefined) delete prefs[k]; else prefs[k] = next[k];
        commit(k);
      }
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
